import {
  SearchQuery,
  closeSearchPanel,
  findNext,
  findPrevious,
  getSearchQuery,
  replaceAll,
  replaceNext,
  search,
  selectMatches,
  setSearchQuery,
} from "@codemirror/search";
import { type Extension } from "@codemirror/state";
import { EditorView, type Panel, runScopeHandlers, type ViewUpdate } from "@codemirror/view";

function button(name: string, label: string, onClick: () => void): HTMLButtonElement {
  const element = document.createElement("button");
  element.type = "button";
  element.name = name;
  element.className = "cm-button";
  element.textContent = label;
  element.addEventListener("click", onClick);
  return element;
}

function checkbox(name: string): HTMLInputElement {
  const element = document.createElement("input");
  element.type = "checkbox";
  element.name = name;
  element.setAttribute("form", "");
  return element;
}

function textField(name: string, placeholder: string): HTMLInputElement {
  const element = document.createElement("input");
  element.name = name;
  element.placeholder = placeholder;
  element.ariaLabel = placeholder;
  element.className = "cm-textfield";
  element.setAttribute("form", "");
  return element;
}

class DbtChartsSearchPanel implements Panel {
  readonly dom: HTMLElement;
  readonly top = true;
  private query: SearchQuery;
  private readonly searchField: HTMLInputElement;
  private readonly replaceField: HTMLInputElement;
  private readonly caseField: HTMLInputElement;
  private readonly regexpField: HTMLInputElement;
  private readonly wordField: HTMLInputElement;
  private readonly replaceRow: HTMLElement;
  private readonly toggleReplace: HTMLButtonElement;

  constructor(private readonly view: EditorView) {
    this.query = getSearchQuery(view.state);
    this.searchField = textField("search", view.state.phrase("Find"));
    this.searchField.setAttribute("main-field", "true");
    this.replaceField = textField("replace", view.state.phrase("Replace"));
    this.caseField = checkbox("case");
    this.regexpField = checkbox("re");
    this.wordField = checkbox("word");

    for (const field of [this.searchField, this.replaceField]) {
      field.addEventListener("change", () => {
        this.commit();
      });
      field.addEventListener("keyup", () => {
        this.commit();
      });
    }
    for (const field of [this.caseField, this.regexpField, this.wordField]) {
      field.addEventListener("change", () => {
        this.commit();
      });
    }

    this.toggleReplace = button("toggleReplace", "›", () => {
      this.setReplaceOpen(this.replaceRow.hidden);
    });
    this.toggleReplace.className = "cm-search-toggle";
    this.toggleReplace.ariaLabel = view.state.phrase("Replace");
    this.toggleReplace.title = view.state.phrase("Replace");
    this.toggleReplace.hidden = view.state.readOnly;

    const findRow = document.createElement("div");
    findRow.className = "cm-search-find";
    findRow.append(
      this.toggleReplace,
      this.searchField,
      button("next", view.state.phrase("next"), () => findNext(view)),
      button("prev", view.state.phrase("previous"), () => findPrevious(view)),
      button("select", view.state.phrase("all"), () => {
        selectMatches(view);
        this.searchField.focus();
        this.searchField.select();
      }),
    );

    const options = document.createElement("div");
    options.className = "cm-search-options";
    for (const [field, label] of [
      [this.caseField, view.state.phrase("match case")],
      [this.regexpField, view.state.phrase("regexp")],
      [this.wordField, view.state.phrase("by word")],
    ] as const) {
      const option = document.createElement("label");
      option.append(field, label);
      options.append(option);
    }
    findRow.append(options);

    this.replaceRow = document.createElement("div");
    this.replaceRow.className = "cm-search-replace";
    this.replaceRow.hidden = true;
    this.replaceRow.append(
      this.replaceField,
      button("replace", view.state.phrase("replace"), () => replaceNext(view)),
      button("replaceAll", view.state.phrase("replace all"), () => replaceAll(view)),
    );

    const close = button("close", "×", () => closeSearchPanel(view));
    close.className = "cm-search-close";
    close.ariaLabel = view.state.phrase("close");

    this.dom = document.createElement("div");
    this.dom.className = "cm-search";
    this.dom.addEventListener("keydown", (event) => {
      this.keydown(event);
    });
    this.dom.append(findRow);
    if (!view.state.readOnly) this.dom.append(this.replaceRow);
    this.dom.append(close);
    this.setReplaceOpen(false);
    this.setQuery(this.query);
  }

  mount(): void {
    this.searchField.select();
  }

  update(update: ViewUpdate): void {
    const query = getSearchQuery(update.state);
    if (!query.eq(this.query)) this.setQuery(query);
  }

  private commit(): void {
    const query = new SearchQuery({
      search: this.searchField.value,
      replace: this.replaceField.value,
      caseSensitive: this.caseField.checked,
      regexp: this.regexpField.checked,
      wholeWord: this.wordField.checked,
      literal: this.query.literal,
      test: this.query.test,
    });
    if (!query.eq(this.query)) {
      this.query = query;
      this.view.dispatch({ effects: setSearchQuery.of(query) });
    }
  }

  private keydown(event: KeyboardEvent): void {
    if (runScopeHandlers(this.view, event, "search-panel")) {
      event.preventDefault();
    } else if (event.key === "Enter" && event.target === this.searchField) {
      event.preventDefault();
      (event.shiftKey ? findPrevious : findNext)(this.view);
    } else if (event.key === "Enter" && event.target === this.replaceField) {
      event.preventDefault();
      replaceNext(this.view);
    }
  }

  private setQuery(query: SearchQuery): void {
    this.query = query;
    this.searchField.value = query.search;
    this.replaceField.value = query.replace;
    this.caseField.checked = query.caseSensitive;
    this.regexpField.checked = query.regexp;
    this.wordField.checked = query.wholeWord;
  }

  private setReplaceOpen(open: boolean): void {
    this.replaceRow.hidden = !open;
    this.toggleReplace.ariaExpanded = String(open);
    this.toggleReplace.classList.toggle("cm-search-toggle-open", open);
  }
}

// The panel takes a row of its own above the scroller (CM6's own docked
// placement) rather than floating over the document. Floating hid whatever sat
// on the first two lines: a match there was scrolled "into view" at the top of
// the viewport, which is underneath the panel, so find on a key near the head
// of a board landed on nothing you could see. Right-aligning inside the row
// keeps the placement the floating version had without taking the row from the
// document.
const searchTheme = EditorView.theme({
  ".cm-panel.cm-search": {
    position: "relative",
    marginLeft: "auto",
    display: "flex",
    flexDirection: "column",
    gap: "6px",
    width: "480px",
    maxWidth: "calc(100% - 8px)",
    boxSizing: "border-box",
    padding: "6px 28px 6px 6px",
    borderLeft: "1px solid #ddd",
    borderBottom: "1px solid #ddd",
    backgroundColor: "inherit",
  },
  ".cm-search-find": {
    display: "grid",
    gridTemplateColumns: "auto minmax(80px, 1fr) auto auto auto",
    alignItems: "center",
    gap: "6px",
  },
  ".cm-search-replace": {
    display: "flex",
    alignItems: "center",
    gap: "6px",
  },
  ".cm-search-replace[hidden]": {
    display: "none",
  },
  ".cm-search-find input, .cm-search-find button, .cm-search-replace input, .cm-search-replace button": {
    margin: "0",
  },
  ".cm-search-options": {
    gridColumn: "2 / -1",
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
  },
  ".cm-search-options label": {
    display: "inline-flex",
    alignItems: "center",
    gap: "3px",
    margin: "0",
    fontSize: "80%",
    whiteSpace: "nowrap",
  },
  ".cm-search-options input[type=checkbox]": {
    margin: "0",
  },
  ".cm-search-toggle": {
    border: "none",
    background: "transparent",
    padding: "2px 4px",
    font: "inherit",
  },
  ".cm-search-toggle-open": {
    transform: "rotate(90deg)",
  },
  ".cm-search-close": {
    position: "absolute",
    top: "4px",
    right: "6px",
    border: "none",
    background: "transparent",
    padding: "0",
    font: "inherit",
  },
});

export function dbtChartsSearch(): Extension {
  return [search({ top: true, createPanel: (view) => new DbtChartsSearchPanel(view) }), searchTheme];
}
